// Domain Models - match backend data structure
export interface StrainReading {
  strainId: number;
  organisationId: number;
  fullName: string;
  date: Date;
  recovery: number;
  strain: number;
  averageHeartRate: number;
  totalWorkouts: number;
  workoutStrain: number;
  percentageOfDailyStrainFromWorkout: number;
  dailyStrainWithoutWorkout: number;
  sports: Sport[];
  optimalStrain: number;
  shipId: number;
}

export interface Sport {
  name: string;
}

export interface StrainZone {
  min: number;
  max: number;
  label: string;
  color: string;
  description: string;
}

export interface StrainMetrics {
  current: number;
  average: number;
  peak: number;
  trend: 'up' | 'down' | 'stable';
  weeklyAverage: number;
  averageHeartRate: number;
  totalWorkouts: number;
  workoutStrainPercentage: number;
}

// Domain Services
export class StrainAnalyzer {
  static getStrainZones(): StrainZone[] {
    return [
      { min: 0, max: 9, label: 'Light', color: '#00C851', description: 'Low cardiovascular load' },
      { min: 10, max: 13, label: 'Moderate', color: '#FFB347', description: 'Building fitness' },
      { min: 14, max: 17, label: 'High', color: '#FF8C00', description: 'Overreaching territory' },
      { min: 18, max: 21, label: 'All Out', color: '#FF4444', description: 'Unsustainable pace' }
    ];
  }

  static determineZone(strain: number): StrainZone {
    const zones = this.getStrainZones();
    return zones.find(zone => strain >= zone.min && strain <= zone.max) || zones[0];
  }

  static calculateTrend(data: number[], timeRange: '7d' | '30d' | '90d'): 'up' | 'down' | 'stable' {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    if (data.length < 4) return 'stable';
    const recent = data.slice(-7).reduce((a, b) => a + b, 0) / 7;
    const previous = data.slice(-days, -7).reduce((a, b) => a + b, 0) / 7;
    const diff = previous - recent;
    return Math.abs(diff) < 0.5 ? 'stable' : diff > 0 ? 'up' : 'down';
  }

  static formatSports(sports: Sport[]): string {
    if (!sports || sports.length === 0) return 'No activities';
    return sports.map(s => s.name).join(', ');
  }

  // Aggregate data by date
  static aggregateDataByDate(data: StrainReading[]): StrainReading[] {
    if (!data || data.length === 0) return [];

    // Group data by date
    const groupedByDate = data.reduce((groups, item) => {
      const dateKey = new Date(item.date).toISOString().split('T')[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {} as Record<string, StrainReading[]>);

    // Aggregate each date group into a single record
    return Object.entries(groupedByDate).map(([dateKey, dayData]) => {
      const validData = dayData.filter(item =>
        item.strain !== null &&
        item.strain !== undefined &&
        !isNaN(item.strain)
      );

      if (validData.length === 0) {
        // Return a default record if no valid data
        return {
          ...dayData[0],
          date: new Date(dateKey),
          strain: 0,
          averageHeartRate: 0,
          totalWorkouts: 0,
          workoutStrain: 0,
          percentageOfDailyStrainFromWorkout: 0,
          sports: []
        };
      }

      // Aggregate all sports from all users for this date
      const allSports = dayData.flatMap(item => item.sports || []);
      const uniqueSports = Array.from(
        new Set(allSports.map(sport => sport.name))
      ).map(name => ({ name }));

      return {
        strainId: dayData[0].strainId, // Use first record's ID
        organisationId: dayData[0].organisationId,
        fullName: `${validData.length} crew members`, // Show how many crew members
        date: new Date(dateKey),
        recovery: validData.reduce((sum, item) => sum + (item.recovery || 0), 0) / validData.length,
        strain: validData.reduce((sum, item) => sum + item.strain, 0) / validData.length, // Average strain
        averageHeartRate: validData.reduce((sum, item) => sum + (item.averageHeartRate || 0), 0) / validData.length,
        totalWorkouts: validData.reduce((sum, item) => sum + (item.totalWorkouts || 0), 0), // Sum all workouts
        workoutStrain: validData.reduce((sum, item) => sum + (item.workoutStrain || 0), 0) / validData.length,
        percentageOfDailyStrainFromWorkout: validData.reduce((sum, item) => sum + (item.percentageOfDailyStrainFromWorkout || 0), 0) / validData.length,
        dailyStrainWithoutWorkout: validData.reduce((sum, item) => sum + (item.dailyStrainWithoutWorkout || 0), 0) / validData.length,
        optimalStrain: validData.reduce((sum, item) => sum + (item.optimalStrain || 0), 0) / validData.length,
        sports: uniqueSports,
        shipId: dayData[0].shipId
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort by date
  }

  static filterDataByTimeRange(data: StrainReading[], timeRange: '7d' | '30d' | '90d'): StrainReading[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(
      Math.max(...data.map((obj: StrainReading) => new Date(obj.date).getTime()))
    );
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return data.filter(item => new Date(item.date) >= cutoffDate);
  }
}
